import api from '@/services/utils/base';
import store from '@/store';
import { sortByPickerData } from '@/views/dashboard/pickers/base/utils';
import _ from 'lodash';
import {loadLanguageAsync} from '@/i18n';
import * as Sentry from '@sentry/vue';


export default class User {
  getUser({ isLoadLanguage = true } = {}) {
    return api
      .get('user')
      .then((res: any) => {
        res.data.payload.workspaces = _.orderBy(res.data.payload.workspaces, ['deletable','name'], ['asc','asc'])
        isLoadLanguage && loadLanguageAsync(res.data.payload.language);
        const {email,id} = res.data.payload;
        Sentry.setUser({
          email,
          id,
        });
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  putUser(payload: any) {
    return api.put('user', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  patchUser(params) {
    return api.patch('user', params)
      .then((res: any) => Promise.resolve(res.data.payload))
      .catch((error: any) => Promise.reject(error))
  }

  getAccessTokens(params?: object) {
    return api.get('user/access-tokens', params).then((res: any) => {
      return Promise.resolve(res.data.payload);
    })
  }

  deleteAllAccessTokens() {
    return api.delete('user/access-tokens').then((res: any) => {
      return Promise.resolve(res.data.payload);
    });
  }

  deleteAccessToken(id: string) {
    return api.delete(`user/access-tokens/${id}`).then((res: any) => {
      return Promise.resolve(res.data.payload);
    })

  }

  getApiKeys(payload?: object) {
    return api
      .get('user/api-keys', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postCreateApiKey({ params }) {
    return api
      .post('user/api-keys',params)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  patchApiKey(id: string, params) {
    return api
      .patch(`user/api-keys/${id}`, params)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  deleteApiKey(id: string) {
    return api.delete(`user/api-keys/${id}`)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  deleteApiKeys() {
    return api.delete(`user/api-keys`);
  }

  getLandingSecretKey() {
    return api
      .get('user/landing-key')
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  create(payload: object) {
    return api.post('auth/register', payload);
  }


  postTwoFactorGetQrCode(payload: object) {
    return api
      .post('/user/two-factor/get-qr-code', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  getTwoFactorRegenerateCodes() {
    return api.get('user/two-factor/new-backup-codes');
  }

  postTwoFactorVerifyQrCode(payload: object) {
    return api
      .post('user/two-factor/verify-qr-code', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postTwoFactorDisable(payload: object) {
    return api
      .post('user/two-factor/disable', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postTwoFactorAuth(payload: object) {
    return api
      .post('auth/two-factor', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  getTwoFactorCountUnusedCodes() {
    return api.get('user/two-factor/count-unused-codes');
  }

  postUploadConversions(payload: object) {
    return api
      .post('user/upload-conversions', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postSendPostbacks(payload: object) {
    return api
      .post('user/send-postbacks', payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postForgotPasswordEmail(payload: object) {
    return api.post('auth/forgot-password', payload);
  }

  putForgotPasswordSetNew(payload: object) {
    const config: any = {
      meta: {
        ignoreInterceptor: true
      }
    };
    return api.put('auth/forgot-password', payload, config);
  }

  postReactivateAccount(payload: object) {
    return api.post('auth/reactivate-account', payload);
  }

  postCreateMember(payload: object) {
    return api.post('user/members', payload).then((res: any) => {
      return Promise.resolve(res.data.payload);
    })
    .catch((err: any) => {
      return Promise.reject(err);
    });
  }

  getMember(id: string) {
    return api.get(`user/members/${id}`);
  }

  getAllMembers() {
    return api.get('user/members')
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  getMemberAcl({userId,workspaceIds}:{userId: string,workspaceIds?: any}) {
    return api.get(`user/members/${userId}/permissions`, {params: {workspaceIds}})
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  deleteMember(id: string) {
    return api.delete(`user/members/${id}`)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  putMember(id: string, payload: object) {
    return api.put(`user/members/${id}`, payload)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  getFindMembers(payload: object) {
    return api.get('user/members', payload);
  }

  getItemsRelations({workspaceId}:{workspaceId: string}) {
    return api.get('user/members/items-relations', {params: {workspaceId}})
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  getTemporaryPassword() {
    return api
      .get('user/support-password')
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  postResetPassword(id: string) {
    return api
      .post(`/user/members/${id}/reset-password`)
      .then((res: any) => {
        return Promise.resolve(res.data.payload);
      })
      .catch((err: any) => {
        return Promise.reject(err);
      });
  }

  updateDashboardQuery(user: any) {
    let sorting = sortByPickerData().find((item) => item.userValue === user.dashboardDefaultGroup);
    if(sorting) {
      let payload = {
        title: sorting.title,
        value: sorting.pickerValue
      };
  
      store.commit('dashboardSortBy', payload);
    }

    store.commit('dashboardDirection', user.dashboardDefaultOrderDir);
  }

  markPaymentNotificationAsRead() {
    return api.post('user/payment-notification-read').then((res: any) => {
      return Promise.resolve(res.data.payload);
    })
    .catch((err: any) => {
      return Promise.reject(err);
    });
  }
}
